import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { toast } from "@/hooks/use-toast";
import { Plus, Trash2, Loader2, Megaphone } from "lucide-react";

interface Notice {
  id: string;
  title: string;
  body: string | null;
  severity: "info" | "warning" | "success" | "critical";
  audience: "all" | "global" | "regional";
  region_id: string | null;
  is_active: boolean;
  starts_at: string;
  ends_at: string | null;
  created_by_name: string | null;
  created_at: string;
}

const SEV_STYLE: Record<string, string> = {
  info: "bg-sky-50 text-sky-900 border-sky-200",
  warning: "bg-amber-50 text-amber-900 border-amber-300",
  success: "bg-emerald-50 text-emerald-900 border-emerald-300",
  critical: "bg-rose-50 text-rose-900 border-rose-300",
};

interface Props { isGlobalAdmin: boolean; userName?: string }

export const NoticesManager = ({ isGlobalAdmin, userName }: Props) => {
  const [rows, setRows] = useState<Notice[]>([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState({ title: "", body: "", severity: "info" as Notice["severity"], audience: "all" as Notice["audience"], ends_at: "" });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    const { data, error } = await (supabase as any)
      .from("admin_notices")
      .select("*")
      .order("created_at", { ascending: false });
    if (error) toast({ title: "Failed to load notices", description: error.message, variant: "destructive" });
    setRows((data || []) as Notice[]);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!draft.title.trim()) return toast({ title: "Title required", variant: "destructive" });
    setBusy(true);
    const { data: { user } } = await supabase.auth.getUser();
    const { error } = await (supabase as any).from("admin_notices").insert({
      title: draft.title.trim(),
      body: draft.body.trim() || null,
      severity: draft.severity,
      audience: draft.audience,
      ends_at: draft.ends_at ? new Date(draft.ends_at).toISOString() : null,
      created_by: user?.id || null,
      created_by_name: userName || user?.email || null,
    });
    setBusy(false);
    if (error) return toast({ title: "Failed", description: error.message, variant: "destructive" });
    toast({ title: "Notice posted" });
    setDraft({ title: "", body: "", severity: "info", audience: "all", ends_at: "" });
    load();
  };

  const toggleActive = async (n: Notice) => {
    const { error } = await (supabase as any).from("admin_notices").update({ is_active: !n.is_active }).eq("id", n.id);
    if (error) return toast({ title: "Update failed", description: error.message, variant: "destructive" });
    load();
  };

  const remove = async (id: string) => {
    if (!confirm("Delete this notice?")) return;
    const { error } = await (supabase as any).from("admin_notices").delete().eq("id", id);
    if (error) return toast({ title: "Delete failed", description: error.message, variant: "destructive" });
    load();
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Megaphone className="w-5 h-5" /> Post a notice for admins</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <Input placeholder="Title (e.g. Site maintenance tonight 10pm CT)" value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} />
          <Textarea placeholder="Optional details…" rows={3} value={draft.body} onChange={(e) => setDraft({ ...draft, body: e.target.value })} />
          <div className="grid sm:grid-cols-3 gap-2">
            <Select value={draft.severity} onValueChange={(v: any) => setDraft({ ...draft, severity: v })}>
              <SelectTrigger><SelectValue placeholder="Severity" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="info">Info</SelectItem>
                <SelectItem value="success">Success</SelectItem>
                <SelectItem value="warning">Warning</SelectItem>
                <SelectItem value="critical">Critical</SelectItem>
              </SelectContent>
            </Select>
            <Select value={draft.audience} onValueChange={(v: any) => setDraft({ ...draft, audience: v })}>
              <SelectTrigger><SelectValue placeholder="Audience" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All admins</SelectItem>
                <SelectItem value="global">Global admins only</SelectItem>
                <SelectItem value="regional">Regional admins only</SelectItem>
              </SelectContent>
            </Select>
            <Input type="datetime-local" value={draft.ends_at} onChange={(e) => setDraft({ ...draft, ends_at: e.target.value })} title="Auto-expire (optional)" />
          </div>
          <Button onClick={create} disabled={busy}><Plus className="w-4 h-4 mr-1" /> Post notice</Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>All notices</CardTitle></CardHeader>
        <CardContent>
          {loading ? (
            <div className="py-8 text-center text-muted-foreground"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>
          ) : rows.length === 0 ? (
            <p className="text-sm text-muted-foreground">No notices yet.</p>
          ) : (
            <ul className="space-y-2">
              {rows.map((n) => (
                <li key={n.id} className={`rounded-md border px-3 py-2 ${SEV_STYLE[n.severity] || SEV_STYLE.info}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <strong className="text-sm">{n.title}</strong>
                        <Badge variant="outline" className="text-[10px]">{n.audience}</Badge>
                        <Badge variant="outline" className="text-[10px]">{n.severity}</Badge>
                        {!n.is_active && <Badge variant="secondary" className="text-[10px]">inactive</Badge>}
                      </div>
                      {n.body && <p className="text-xs mt-1 opacity-90 whitespace-pre-wrap">{n.body}</p>}
                      <p className="text-[10px] opacity-70 mt-1">
                        {new Date(n.created_at).toLocaleString()}{n.created_by_name ? ` · ${n.created_by_name}` : ""}
                        {n.ends_at ? ` · ends ${new Date(n.ends_at).toLocaleString()}` : ""}
                      </p>
                    </div>
                    {isGlobalAdmin && (
                      <div className="flex items-center gap-2 shrink-0">
                        <Switch checked={n.is_active} onCheckedChange={() => toggleActive(n)} />
                        <Button size="icon" variant="ghost" onClick={() => remove(n.id)}><Trash2 className="w-4 h-4" /></Button>
                      </div>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default NoticesManager;
